import React, { Component } from "react";
import { withRouter } from "react-router-dom";
import axios from "axios";
import { Store } from "react-notifications-component";
import { ArrowLeft, Image as ImageIcon, AlertCircle } from "lucide-react";

import AdminPageShell from "./AdminPageShell";
import setAuthToken from "../../utils/setAuthToken";

import {
  PageHeader,
  Card,
  Input,
  Textarea,
  NumberInput,
  Select,
  FormField,
  TagInput,
  Button,
  Spinner,
} from "../../design-system";

const TYPE_OPTIONS = [
  { value: "JOB",            label: "Emploi" },
  { value: "PROJECT",        label: "Projet" },
  { value: "CERTIFICATION",  label: "Certification" },
  { value: "EDUCATION",      label: "Formation" },
  { value: "OTHER",          label: "Autre" },
];

class ProfessionalForm extends Component {
  constructor(props) {
    super(props);
    const { id } = this.props.match.params;
    this.state = {
      id: id || null,
      title: "",
      company: "",
      location: "",
      period: "",
      description: "",
      type: "JOB",
      technologies: [],
      imageUrl: "",
      order: 0,
      loading: false,
      saving: false,
      imagePreview: null,
      imageError: false,
    };
  }

  handleImageUrlChange = (e) => {
    const imageUrl = e.target.value;
    this.setState({ imageUrl, imageError: false, imagePreview: imageUrl || null });
    if (imageUrl) {
      const img = document.createElement("img");
      img.onload  = () => this.setState({ imagePreview: imageUrl, imageError: false });
      img.onerror = () => this.setState({ imagePreview: null, imageError: true });
      img.src = imageUrl;
    }
  };

  componentDidMount() {
    const token = localStorage.getItem("jwtToken");
    if (token) setAuthToken(token);
    if (this.state.id) this.loadProfessional();
  }

  loadProfessional = async () => {
    try {
      this.setState({ loading: true });
      const response = await axios.get(`/api/professionals/id/${this.state.id}`);
      const p = response.data.professional;
      this.setState({
        title: p.title || "",
        company: p.company || "",
        location: p.location || "",
        period: p.period || "",
        description: p.description || "",
        type: p.type || "JOB",
        technologies: Array.isArray(p.technologies) ? p.technologies : [],
        imageUrl: p.imageUrl || "",
        order: p.order || 0,
        loading: false,
        imagePreview: p.imageUrl || null,
        imageError: false,
      });
    } catch (error) {
      console.error("Error loading professional:", error);
      this.setState({ loading: false });
      Store.addNotification({
        title: "Erreur", message: "Impossible de charger le professionnel",
        type: "danger", insert: "top", container: "top-right", dismiss: { duration: 3000 },
      });
    }
  };

  handleSubmit = async (e) => {
    e.preventDefault();
    this.setState({ saving: true });
    try {
      const token = localStorage.getItem("jwtToken");
      if (token) setAuthToken(token);

      const data = {
        title: this.state.title,
        company: this.state.company,
        location: this.state.location,
        period: this.state.period,
        description: this.state.description,
        type: this.state.type,
        technologies: this.state.technologies,
        imageUrl: this.state.imageUrl,
        order: parseInt(this.state.order) || 0,
      };

      if (this.state.id) await axios.put(`/api/professionals/${this.state.id}`, data);
      else                await axios.post("/api/professionals", data);

      Store.addNotification({
        title: `Professionnel ${this.state.id ? "modifié" : "créé"}`,
        message: `« ${this.state.title} » enregistré.`,
        type: "success", insert: "top", container: "top-right", dismiss: { duration: 3000 },
      });
      this.props.history.push("/dashboard/professionals");
    } catch (error) {
      console.error("Error saving professional:", error);
      Store.addNotification({
        title: "Erreur",
        message: error.response?.data?.message || "Erreur lors de la sauvegarde",
        type: "danger", insert: "top", container: "top-right", dismiss: { duration: 5000 },
      });
      this.setState({ saving: false });
    }
  };

  render() {
    const { loading, saving } = this.state;
    const isEdit = !!this.state.id;
    const pageTitle = isEdit ? "Modifier le professionnel" : "Nouveau professionnel";

    if (loading) {
      return (
        <AdminPageShell pageTitle={pageTitle}>
          <div className="flex items-center justify-center py-20 text-mute"><Spinner size="lg" /></div>
        </AdminPageShell>
      );
    }

    return (
      <AdminPageShell pageTitle={pageTitle} breadcrumbLast={isEdit ? (this.state.title || "Modifier") : undefined}>
        <PageHeader
          eyebrow="— Professionnels"
          title={pageTitle}
          description={isEdit ? "Mets à jour cette entrée." : "Emploi, projet, certif ou formation."}
          actions={
            <Button
              variant="secondary"
              leftIcon={<ArrowLeft className="w-4 h-4" strokeWidth={1.8} />}
              onClick={() => this.props.history.push("/dashboard/professionals")}
            >
              Retour
            </Button>
          }
        />

        <form onSubmit={this.handleSubmit}>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
            <div className="lg:col-span-2 space-y-5">
              <Card>
                <Card.Header><Card.Title>Informations</Card.Title></Card.Header>
                <div className="space-y-4">
                  <FormField label="Titre" required>
                    <Input
                      value={this.state.title}
                      onChange={(e) => this.setState({ title: e.target.value })}
                      required autoFocus
                    />
                  </FormField>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormField label="Type" required>
                      <Select
                        value={this.state.type}
                        onChange={(e) => this.setState({ type: e.target.value })}
                        options={TYPE_OPTIONS}
                        required
                      />
                    </FormField>
                    <FormField label="Période" helper="Ex. 2023-2024">
                      <Input
                        value={this.state.period}
                        onChange={(e) => this.setState({ period: e.target.value })}
                      />
                    </FormField>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormField label="Entreprise / Organisme">
                      <Input
                        value={this.state.company}
                        onChange={(e) => this.setState({ company: e.target.value })}
                      />
                    </FormField>
                    <FormField label="Localisation">
                      <Input
                        value={this.state.location}
                        onChange={(e) => this.setState({ location: e.target.value })}
                      />
                    </FormField>
                  </div>
                  <FormField label="Description">
                    <Textarea
                      rows={6}
                      value={this.state.description}
                      onChange={(e) => this.setState({ description: e.target.value })}
                    />
                  </FormField>
                  <FormField label="Technologies">
                    <TagInput
                      value={this.state.technologies}
                      onChange={(technologies) => this.setState({ technologies })}
                      placeholder="HTML, CSS, React…"
                    />
                  </FormField>
                </div>
              </Card>
            </div>

            <div className="space-y-5">
              <Card>
                <Card.Header><Card.Title>Options</Card.Title></Card.Header>
                <div className="space-y-4">
                  <FormField label="Image (URL)" hint={<ImageIcon className="w-3.5 h-3.5" />}>
                    <Input
                      type="url"
                      placeholder="https://…"
                      value={this.state.imageUrl}
                      onChange={this.handleImageUrlChange}
                    />
                    {this.state.imageUrl && (
                      <div className="mt-2">
                        {this.state.imageError ? (
                          <div className="flex items-start gap-2 p-3 rounded-md bg-danger-bg text-danger text-xs">
                            <AlertCircle className="w-4 h-4 shrink-0" strokeWidth={1.8} />
                            Impossible de charger l'image
                          </div>
                        ) : this.state.imagePreview ? (
                          <img
                            src={this.state.imagePreview}
                            alt="Aperçu"
                            className="w-full max-h-40 object-cover rounded-md border border-border"
                            onError={() => this.setState({ imageError: true, imagePreview: null })}
                          />
                        ) : null}
                      </div>
                    )}
                  </FormField>
                  <FormField label="Ordre" helper="0 = premier.">
                    <NumberInput
                      value={Number(this.state.order) || 0}
                      onChange={(value) => this.setState({ order: value })}
                      min={0}
                    />
                  </FormField>
                </div>
              </Card>

              <Card variant="filled">
                <div className="space-y-2">
                  <Button type="submit" fullWidth isLoading={saving}>
                    {isEdit ? "Enregistrer" : "Créer"}
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    fullWidth
                    onClick={() => this.props.history.push("/dashboard/professionals")}
                  >
                    Annuler
                  </Button>
                </div>
              </Card>
            </div>
          </div>
        </form>
      </AdminPageShell>
    );
  }
}

export default withRouter(ProfessionalForm);
